import { useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LayoutGrid, ShieldCheck, LogOut, Users, MessageCircle, Wallet, BookOpen, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { LOGO_URL, LEGACY_LOGO_URL } from "@/lib/api";
import { isLegacyPath } from "@/lib/theme";
import { useShellActive } from "@/components/shell/useShell";

export const manualeFor = (path) => {
  if (path.startsWith("/manuale")) return null;
  const map = [
    ["/contabilita", { route: "/manuale/contabilita" }],
    ["/personale", { route: "/manuale/personale" }],
    ["/compliance", { route: "/manuale/compliance" }],
    ["/chi-fa-cosa", { route: "/manuale/chi-fa-cosa" }],
    ["/gestione-agenzia", { route: "/manuale/gestione-agenzia" }],
    ["/mup", { route: "/manuale/mup" }],
    ["/cauzioni", { route: "/manuale/cauzioni" }],
    ["/chat", { route: "/manuale/chat" }],
    ["/foglio-cassa", { route: "/manuale/foglio-cassa" }],
    ["/easytrasporto", { route: "/manuale/easytrasporto" }],
    ["/sinistr", { file: "Manuale_Operatore_Sinistri" }],
    ["/modulo-sinistri", { file: "Manuale_Operatore_Sinistri" }],
  ];
  const hit = map.find(([p]) => path.startsWith(p));
  return hit ? hit[1] : null;
};

export const Header = () => {
  const { user, logout, apps } = useAuth();
  const { isInternal: chatInternal, unread } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
  const isInternal = user?.role === "admin" || (apps || []).some((a) => !a.benefit_ets);

  const shellActive = useShellActive();
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const path = location.pathname;
  const manualeCorrente = manualeFor(path);
  const openManuale = () => {
    if (!manualeCorrente) return;
    if (manualeCorrente.route) navigate(manualeCorrente.route);
    else window.open(`/manuali/${manualeCorrente.file}.pdf`, "_blank");
  };

  if (shellActive) return null;

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <button
          data-testid="header-logo-btn"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-3 group"
        >
          <img src={isLegacyPath(path) ? LEGACY_LOGO_URL : LOGO_URL} alt="Assietica Suite" className={isLegacyPath(path) ? "h-12 w-auto object-contain" : "h-11 w-auto object-contain"} data-testid="header-logo" />
        </button>

        <div className="flex items-center gap-2">
          {location.pathname !== "/dashboard" && (
            <button
              data-testid="nav-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-300"
            >
              <LayoutGrid className="h-4 w-4" /> Dashboard
            </button>
          )}
          {isInternal && location.pathname !== "/anagrafiche" && (
            <button
              data-testid="nav-anagrafiche-btn"
              onClick={() => navigate("/anagrafiche")}
              className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-300"
            >
              <Users className="h-4 w-4" /> Anagrafiche
            </button>
          )}
          {user?.role === "admin" && location.pathname !== "/admin" && (
            <button
              data-testid="nav-admin-btn"
              onClick={() => navigate("/admin")}
              className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-300"
            >
              <ShieldCheck className="h-4 w-4" /> Admin
            </button>
          )}

          {(user?.role === "admin" || (apps || []).some((a) => a.route === "/foglio-cassa")) && location.pathname !== "/foglio-cassa" && (
            <button
              data-testid="nav-foglio-cassa-btn"
              onClick={() => navigate("/foglio-cassa")}
              className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-300"
            >
              <Wallet className="h-4 w-4" /> Foglio Cassa
            </button>
          )}
          {manualeCorrente && (
            <button
              data-testid="nav-manuale-btn"
              onClick={openManuale}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-300"
              title="Manuale operatore del modulo corrente"
            >
              <BookOpen className="h-4 w-4" /> <span className="hidden md:inline">Manuale</span>
            </button>
          )}
          {chatInternal && location.pathname !== "/chat" && (
            <button
              data-testid="nav-chat-btn"
              onClick={() => navigate("/chat")}
              className="relative flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-300"
            >
              <MessageCircle className="h-4 w-4" /> <span className="hidden sm:inline">Chat</span>
              {(unread.total || 0) > 0 && (
                <span data-testid="header-chat-badge" className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${unread.alte_aperte > 0 ? "bg-red-600" : "bg-red-500"}`}>
                  {unread.total > 99 ? "99+" : unread.total}
                </span>
              )}
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full p-0.5 hover:ring-2 hover:ring-slate-200 transition-all">
                <Avatar className="h-9 w-9 border border-slate-200">
                  {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
                  <AvatarFallback className="bg-slate-100 text-slate-700 text-xs font-medium">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex flex-col gap-0.5">
                <span className="font-medium truncate">{user?.name}</span>
                <span className="text-xs text-slate-500 font-normal truncate">{user?.email}</span>
                <span className="text-[10px] uppercase tracking-widest text-brand-blue mt-1">
                  {user?.role === "admin" ? "Amministratore" : "Utente"}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-dashboard"
                onClick={() => navigate("/dashboard")}
                className="cursor-pointer"
              >
                <LayoutGrid className="h-4 w-4 mr-2" /> Dashboard
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isInternal && (
                <DropdownMenuItem
                  data-testid="menu-anagrafiche"
                  onClick={() => navigate("/anagrafiche")}
                  className="cursor-pointer"
                >
                  <Users className="h-4 w-4 mr-2" /> Anagrafiche
                </DropdownMenuItem>
              )}
              {isInternal && (
                <DropdownMenuItem
                  data-testid="menu-archivio-novita"
                  onClick={() => navigate("/novita")}
                  className="cursor-pointer"
                >
                  <Sparkles className="h-4 w-4 mr-2" /> Archivio novità
                </DropdownMenuItem>
              )}
              {user?.role === "admin" && (
                <DropdownMenuItem
                  data-testid="menu-admin"
                  onClick={() => navigate("/admin")}
                  className="cursor-pointer"
                >
                  <ShieldCheck className="h-4 w-4 mr-2" /> Pannello Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-logout"
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
              >
                <LogOut className="h-4 w-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
